import React, { useState, useCallback } from 'react';

import Add from '@mui/icons-material/Add';
import { Grid, Button, Box, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
    commonStyles,
    useRedirectTo,
    useSafeIntl,
} from 'bluesquare-components';
import { DisplayIfUserHasPerm } from 'Iaso/components/DisplayIfUserHasPerm';
import { SearchButton } from 'Iaso/components/SearchButton';
import { baseUrls } from 'Iaso/constants/urls';
import { PlanningsDropdown } from 'Iaso/domains/plannings/components/PlanningsDropdown';
import { userHasOneOfPermissions } from 'Iaso/domains/users/utils';
import { useQueryString } from 'Iaso/hooks/useApiParams';
import { useFilterState } from 'Iaso/hooks/useFilterState';
import { PLANNING_READ, PLANNING_WRITE } from 'Iaso/utils/permissions';
import * as Permission from 'Iaso/utils/permissions';
import { useCurrentUser } from 'Iaso/utils/usersUtils';
import DownloadButtonsComponent from '../../../components/DownloadButtonsComponent';
import InputComponent from '../../../components/forms/InputComponent';
import { useGetOrgUnitTypesDropdownOptions } from '../../orgUnits/orgUnitTypes/hooks/useGetOrgUnitTypesDropdownOptions';
import { useGetProjectsDropdownOptions } from '../../projects/hooks/requests';
import { baseUrl } from '../config';
import { FormResponse, tableDefaults } from '../hooks/useGetForms';
import MESSAGES from '../messages';
import { FormsParams } from '../types/forms';
const dwnldBaseUrl = '/api/forms';
const useStyles = makeStyles(theme => ({
    ...commonStyles(theme),
}));

type Props = {
    params: FormsParams;
    forms?: FormResponse;
    isLoadingForms: boolean;
};

const Filters = ({ params, forms, isLoadingForms }: Props) => {
    const classes: Record<string, string> = useStyles();
    const { formatMessage } = useSafeIntl();
    const redirectTo = useRedirectTo();
    const { filters, handleSearch, handleChange } = useFilterState({
        baseUrl,
        params,
        withPagination: false,
        searchAlwaysEnabled: true,
    });
    const [textSearchError, setTextSearchError] = useState<boolean>(false);
    const currentUser = useCurrentUser();
    const hasPlanningPermission = userHasOneOfPermissions(
        [PLANNING_READ, PLANNING_WRITE],
        currentUser,
    );
    const handleOnlyDeleted = useCallback(
        (key, value) => {
            const valueForParam = value ? '1' : undefined;
            handleChange(key, valueForParam);
        },
        [handleChange],
    );
    const { data: orgUnitTypes, isFetching: isFetchingOuTypes } =
        useGetOrgUnitTypesDropdownOptions();
    const { data: allProjects, isFetching: isFetchingProjects } =
        useGetProjectsDropdownOptions();

    const theme = useTheme();
    const isLargeLayout = useMediaQuery(theme.breakpoints.up('md'));

    const handleAddForm = useCallback(() => {
        redirectTo(baseUrls.formDetail, {
            formId: '0',
        });
    }, [redirectTo]);

    const downloadQueryString = useQueryString(
        { ...params, all: 'true' },
        tableDefaults,
    );
    const csvUrl = `${dwnldBaseUrl}/?${downloadQueryString}&csv=true`;
    const xlsxUrl = `${dwnldBaseUrl}/?${downloadQueryString}&xlsx=true`;

    return (
        <Grid container>
            <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} md={3}>
                    <InputComponent
                        keyValue="search"
                        onChange={handleChange}
                        value={filters.search}
                        type="search"
                        label={MESSAGES.search}
                        blockForbiddenChars
                        onEnterPressed={handleSearch}
                        onErrorChange={setTextSearchError}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <InputComponent
                        keyValue="projectsIds"
                        onChange={handleChange}
                        value={filters.projectsIds}
                        type="select"
                        options={allProjects}
                        label={MESSAGES.projects}
                        loading={isFetchingProjects}
                        onEnterPressed={handleSearch}
                        clearable
                        multi
                    />
                </Grid>

                <Grid item xs={12} md={3}>
                    <InputComponent
                        type="select"
                        onChange={handleChange}
                        keyValue="orgUnitTypeIds"
                        multi
                        label={MESSAGES.orgUnitsTypes}
                        value={filters.orgUnitTypeIds}
                        loading={isFetchingOuTypes}
                        options={orgUnitTypes ?? []}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <PlanningsDropdown
                        handleChange={handleChange}
                        value={filters.planning}
                        keyValue="planning"
                        multi
                    />
                    {!hasPlanningPermission && (
                        <InputComponent
                            keyValue="onlyDeleted"
                            onChange={handleOnlyDeleted}
                            value={filters.onlyDeleted === '1'}
                            type="checkbox"
                            label={MESSAGES.onlyDeleted}
                        />
                    )}
                </Grid>
            </Grid>
            <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} md={3}>
                    {hasPlanningPermission && (
                        <InputComponent
                            keyValue="onlyDeleted"
                            onChange={handleOnlyDeleted}
                            value={filters.onlyDeleted === '1'}
                            type="checkbox"
                            label={MESSAGES.onlyDeleted}
                        />
                    )}
                </Grid>
                <Grid item xs={12} md={9}>
                    <Box
                        mt={isLargeLayout ? 3 : 0}
                        display="flex"
                        justifyContent="flex-end"
                    >
                        <DisplayIfUserHasPerm permissions={[Permission.FORMS]}>
                            <Button
                                variant="outlined"
                                className={classes.button}
                                color="primary"
                                onClick={handleAddForm}
                                data-test="add-form-button"
                                sx={{
                                    mr: 2,
                                }}
                            >
                                <Add className={classes.buttonIcon} />
                                {formatMessage(MESSAGES.addForm)}
                            </Button>
                        </DisplayIfUserHasPerm>
                        <SearchButton
                            disabled={textSearchError}
                            onSearch={handleSearch}
                        />
                    </Box>
                    <Box mt={2} display="flex" justifyContent="flex-end">
                        <DownloadButtonsComponent
                            variant="outlined"
                            xlsxUrl={xlsxUrl}
                            csvUrl={csvUrl}
                            disabled={isLoadingForms || !forms?.forms?.length}
                        />
                    </Box>
                </Grid>
            </Grid>
        </Grid>
    );
};

export { Filters };
